import type { PricingConfig } from '../src/types/pricing.js'
import type { Bed } from '../src/components/bliss/design/configurator/room/beds.js'
import { cabinBookshelf, customGeometry, customVerticalProfile, CUSTOM } from '../src/components/bliss/design/configurator/room/custom-layout.js'

export type CabinMillworkLine = {
  label: string; wood: number; paint: number; labor: number; laborHours: number; price: number
}
/** Owner's Oct 5 revision for these additions: (wood + paint + labor) × 10.
 * Planning takeoff, with 20% wood waste and two finish coats; not a cut list.
 * Uses server-owned pricing, never cost or selling-price inputs from the browser.
 */
export function cabinMillworkPricing(bed: Bed, pricing: PricingConfig, unfinished = false, crown = false): CabinMillworkLine[] {
  if (!bed.builtInBookshelf && !bed.lowerShiplap && !crown) return []
  if (bed.kind !== 'custom' || bed.customStyle !== 'divider') throw new Error('Review the cabin millwork selections.')
  if (![bed.width,bed.depth].every(v=>Number.isFinite(v)&&v>=12&&v<=1200)) throw new Error('Review the cabin dimensions.')
  const height=customVerticalProfile(bed).deckHeight-CUSTOM.frameDepth
  const lines:CabinMillworkLine[]=[]
  const valid=(n:number)=>{if(!Number.isFinite(n)||n<0)throw new Error('Confirm the wood, finish and labor cost settings.');return n}
  const finish=pricing.materials.finish
  const paintPerFoot=unfinished ? 0 : valid(finish.primer.cost)/validCoverage(finish.primer.coverage)+2*valid(finish.paintCustom.cost)/validCoverage(finish.paintCustom.coverage)
  function validCoverage(n:number){if(!Number.isFinite(n)||n<=0)throw new Error('Confirm finish coverage settings.');return n}
  function add(label:string,wood:number,paintArea:number,hours:number){
    const paint=paintArea*paintPerFoot, labor=hours*valid(pricing.laborRate)
    lines.push({label,wood,paint,labor,laborHours:hours,price:Math.round((wood+paint+labor)*10)})
  }
  if (bed.builtInBookshelf) {
    if(bed.bookshelfStyle!==undefined&&!['standard','recessed_arch'].includes(bed.bookshelfStyle))throw new Error('Review the library style.')
    const s=cabinBookshelf(bed)
    if(!s)throw new Error('Move the lower beds apart to fit the selected library before pricing.')
    const arch=bed.bookshelfStyle==='recessed_arch'
    const front=Math.max(...customGeometry(bed).uppers.map(r=>r.y+r.h))
    // Carcass sides, shelves and tops; arch adds two niche faces, core and curved sweep skins.
    const panelArea=(2*height*12+4*s.w*12+(arch ? 2*height*12+s.w*height+2*(front-12)*(height-12)*.4 : 0))/144
    const backArea=s.w*height/144
    const wood=1.2*(panelArea/32*valid(pricing.materials.plywood['11/16"'])+backArea/32*valid(pricing.materials.plywood['1/4"']))
    const hours=(arch ? 14 : 6)+(unfinished ? 0 : 2)
    add(arch ? 'Recessed arch library' : 'Built-in bookshelf',wood,2*(panelArea+backArea),hours)
  }
  if (bed.lowerShiplap) {
    const end=Math.min(...customGeometry(bed).ladders.map(l=>l.y))
    const area=(bed.width+2*end)*height/144
    // 3/4-inch poplar horizontal courses: sqft × thickness = board feet.
    const wood=area*.75*1.2*valid(pricing.materials.lumber.poplar)
    const hours=3+area/12+(unfinished ? 0 : 2)
    add('Accent wall shiplap',wood,area*2,hours)
  }
  if (crown) {
    const uppers=customGeometry(bed).uppers
    const length=Math.max(...uppers.map(r=>r.x+r.w))-Math.min(...uppers.map(r=>r.x))
    // Conservative 7 x 3-inch poplar blank for the modeled profile, including 20% waste.
    const wood=length*7*3/144*1.2*valid(pricing.materials.lumber.poplar)
    const paintArea=length*20/144
    add('7″ crown molding',wood,paintArea,4+length/96+(unfinished ? 0 : 1))
  }
  return lines
}

